import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Alert,
  Divider,
  CircularProgress
} from '@mui/material';
import {
  CloudUpload,
  CloudOff,
  CheckCircle
} from '@mui/icons-material';
import { getDriveAuthUrl } from '../api';

const GoogleDriveConnectionStep = ({ onDecision, decision, isConnected }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleConnect = async () => {
    setLoading(true);
    setError('');
    
    try {
      // Get the Google Drive authentication URL
      const result = await getDriveAuthUrl();
      
      // Redirect to Google OAuth
      window.location.href = result.auth_url;
    } catch (error) {
      console.error('Failed to get Google Drive auth URL:', error);
      setError('Failed to connect to Google Drive. Please try again.');
      setLoading(false);
    }
  };

  const handleSkip = () => {
    onDecision('skip');
  };

  if (isConnected) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="success" sx={{ mb: 2 }}>
          <Typography variant="body2">
            ✅ Google Drive is connected and ready to save your scraped content!
          </Typography>
        </Alert>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          You can now proceed to enter a website URL. Your scraped content will be automatically saved to Google Drive.
        </Typography>
        <Button
          variant="contained"
          onClick={() => onDecision('connected')}
          startIcon={<CheckCircle />}
          sx={{ mt: 1 }}
        >
          Continue to URL Entry
        </Button>
      </Box>
    );
  }

  if (decision === 'skip') {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2">
            ℹ️ Google Drive connection skipped. Content will be saved locally only.
          </Typography>
        </Alert>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          You can proceed to enter a website URL. Your scraped content will be saved locally and can be downloaded as a ZIP file.
        </Typography>
        <Button
          variant="contained"
          onClick={() => onDecision('skipped')}
          sx={{ mt: 1 }}
        >
          Continue to URL Entry
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom>
        Connect to Google Drive (Optional)
      </Typography>
      
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        You can connect to Google Drive to automatically save your scraped content as Google Docs. 
        This makes it easy to organize and share your scraped content.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <CloudUpload sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="subtitle1">
              Connect to Google Drive
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            • Automatically save scraped content as Google Docs<br/>
            • Organize content in folders<br/>
            • Easy sharing and collaboration<br/>
            • Access from anywhere
          </Typography>
          <Button
            variant="contained"
            onClick={handleConnect}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : <CloudUpload />}
            fullWidth
            sx={{ mb: 1 }}
          >
            {loading ? 'Connecting...' : 'Connect to Google Drive'}
          </Button>
        </CardContent>
      </Card>

      <Divider sx={{ my: 2 }}>
        <Typography variant="caption" color="text.secondary">
          OR
        </Typography>
      </Divider>

      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <CloudOff sx={{ mr: 1, color: 'text.secondary' }} />
            <Typography variant="subtitle1">
              Skip Google Drive Connection
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            • Save content locally as files<br/>
            • Download as ZIP archive<br/>
            • No external dependencies<br/>
            • Works offline
          </Typography>
          <Button
            variant="outlined"
            onClick={handleSkip}
            startIcon={<CloudOff />}
            fullWidth
          >
            Skip Google Drive
          </Button>
        </CardContent>
      </Card>

      <Alert severity="warning" sx={{ mt: 2 }}>
        <Typography variant="caption">
          <strong>Note:</strong> Once you start the scraping workflow, you won't be able to connect to Google Drive later. 
          Make your decision now to avoid workflow interruptions.
        </Typography>
      </Alert>
    </Box>
  );
};

export default GoogleDriveConnectionStep;
